import React, { useEffect, useState, useRef, useCallback } from 'react';
import styles from './index.module.scss';
import { getImageFromS3 } from '../../../imageService';
import mgmService from '../../../services/mgmService';

const PointHistoryComponent = ({ setScreenNumber, onQuestionClick }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [balance, setBalance] = useState(0);
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalAccrued, setTotalAccrued] = useState(0);
  const [totalSpent, setTotalSpent] = useState(0);

  const observer = useRef();
  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const months = [
      'січня',
      'лютого',
      'березня',
      'квітня',
      'травня',
      'червня',
      'липня',
      'серпня',
      'вересня',
      'жовтня',
      'листопада',
      'грудня'
    ];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day} ${month} ${hours}:${minutes}`;
  };

  const fetchHistory = useCallback(async (pageNum, tab) => {
    setLoading(true);
    try {
      let operationType = undefined;
      if (tab === 'accrual') operationType = 'REGISTRATION';
      else if (tab === 'expense') operationType = 'CERTIFICATE';

      const response = await mgmService.getBalanceHistory(
        pageNum,
        100,
        operationType
      );

      const items = Array.isArray(response) ? response : response?.items || [];

      if (pageNum === 1 && tab === 'all') {
        const accrued = items.reduce(
          (acc, item) => (item.amount > 0 ? acc + item.amount : acc),
          0
        );
        setTotalAccrued(accrued);

        const spent = items.reduce(
          (acc, item) => (item.amount < 0 ? acc + Math.abs(item.amount) : acc),
          0
        );
        setTotalSpent(spent);
      }

      const formattedItems = items.map((item) => ({
        id: item.id,
        type: item.amount > 0 ? 'accrual' : 'expense',
        title: item.amount > 0 ? 'Нарахування балів' : 'Витрачено балів',
        date: formatDate(item.created_at),
        amount: item.amount > 0 ? `+${item.amount}` : item.amount
      }));

      setHistory((prev) =>
        pageNum === 1 ? formattedItems : [...prev, ...formattedItems]
      );

      setHasMore(items.length === 100);
    } catch (e) {
      console.error('Error fetching history:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const balanceData = await mgmService.getBalance();
      setBalance(balanceData?.balance || 0);
    })();
  }, []);

  useEffect(() => {
    setPage(1);
    setHistory([]);
    setHasMore(true);
  }, [activeTab]);

  useEffect(() => {
    fetchHistory(page, activeTab);
  }, [page, activeTab, fetchHistory]);

  return (
    <div className={styles.container}>
      <img
        src={getImageFromS3('/images/mgm/bg.png')}
        alt="background"
        className={styles.backgroundImage}
      />
      <img
        className={styles.glassHeadBg}
        src={getImageFromS3('/images/mgm/glass-head-bg.png')}
        alt="glass-head"
      />
      <div className={styles.content}>
        <div className={styles.header}>
          <svg
            width="20"
            height="18"
            viewBox="0 0 20 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => setScreenNumber(1)}
          >
            <path
              d="M4.29675 8.59375L9.66769 13.9647C10.261 14.5579 10.261 15.5198 9.66769 16.1131C9.07444 16.7063 8.11258 16.7063 7.51932 16.1131L0.707108 9.30086C0.316583 8.91033 0.316583 8.27717 0.707107 7.88664L7.51932 1.07443C8.11258 0.481175 9.07444 0.481175 9.66769 1.07443C10.261 1.66769 10.261 2.62955 9.66769 3.22281L4.29675 8.59375Z"
              fill="white"
            />
            <rect
              x="2.78076"
              y="6.99902"
              width="17"
              height="3"
              rx="1.5"
              fill="white"
            />
          </svg>
          <div onClick={onQuestionClick}>
            Є питання?
            <svg
              width="27"
              height="21"
              viewBox="0 0 27 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.86207 0C0.837931 0 0 0.821739 0 1.82609V16.4348L2.7931 13.6957H20.4828C21.5069 13.6957 22.3448 12.8739 22.3448 11.8696V1.82609C22.3448 0.821739 21.5069 0 20.4828 0H1.86207ZM23.2759 7.30435V11.8696C23.2759 13.4217 22.0655 14.6087 20.4828 14.6087H13.0345V16.4348C13.0345 17.4391 13.8724 18.2609 14.8966 18.2609H24.2069L27 21V9.13043C27 8.12609 26.1621 7.30435 25.1379 7.30435H23.2759Z"
                fill="white"
              />
            </svg>
          </div>
        </div>
        <div className={styles.pointBalanceContainer}>
          <p>
            Баланс балів <b>{balance}</b>
          </p>
        </div>
        <h1>Історія балів</h1>
        <div className={styles.pointsContainer}>
          <div className={styles.pointsCounter}>
            <img
              src={getImageFromS3('/images/mgm/purple-glass.png')}
              alt="purple-glass"
            />
            <div className={styles.textContainer}>
              <p className={styles.textTitle}>Нараховано</p>
              <p className={styles.textCount}>{totalAccrued}</p>
            </div>
          </div>
          <div className={styles.pointsCounter}>
            <img
              src={getImageFromS3('/images/mgm/red-glass.png')}
              alt="red-glass"
            />
            <div className={styles.textContainer}>
              <p className={styles.textTitle}>Витрачено</p>
              <p className={styles.textCount}>{totalSpent}</p>
            </div>
          </div>
        </div>
        <div className={styles.tabsContainer}>
          <button
            className={activeTab === 'all' ? styles.activeTab : ''}
            onClick={() => setActiveTab('all')}
          >
            Всі
          </button>
          <button
            className={activeTab === 'accrual' ? styles.activeTab : ''}
            onClick={() => setActiveTab('accrual')}
          >
            Нарахування
          </button>
          <button
            className={activeTab === 'expense' ? styles.activeTab : ''}
            onClick={() => setActiveTab('expense')}
          >
            Витрати
          </button>
        </div>
        <div className={styles.historyList}>
          {history.map((item, index) => (
            <div
              key={`${item.id}-${index}`}
              ref={index === history.length - 1 ? lastItemRef : null}
              className={`${styles.historyItem} ${
                item.type === 'expense' ? styles.expenseItem : ''
              }`}
            >
              <div className={styles.itemInfo}>
                <p className={styles.itemTitle}>{item.title}</p>
                <p className={styles.itemDate}>{item.date}</p>
              </div>
              <p className={styles.itemAmount}>{item.amount}</p>
            </div>
          ))}
          {loading && <p className={styles.loading}>Завантаження...</p>}
        </div>
        <div className={styles.footer}>
          <button onClick={() => setScreenNumber(2)}>Правила програм</button>
          <button
            onClick={() =>
              window.open(
                'https://www.pmiprivacy.com/ukraine/uk/consumer/',
                '_blank'
              )
            }
          >
            Політика конфіденційності
          </button>
          <button onClick={() => setScreenNumber(10)}>FAQ</button>
          <button onClick={() => setScreenNumber(11)}>
            Параметри застосування файлів cookie
          </button>
        </div>
      </div>
    </div>
  );
};

export default PointHistoryComponent;
